import { useSignIn, useSignUp } from '@clerk/react';
import { Button, Stack } from '@reelplicate/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useRedirectTarget, withRedirect } from './redirect-target';

/** Figma Mark/Google (101:133): the exported group, inset in its box as in Figma. */
function GoogleMark({ className }: { className?: string }) {
  return (
    <span className={`relative size-5 shrink-0 ${className ?? ''}`}>
      <img
        src="/auth/google.svg"
        alt=""
        className="absolute top-[4.17%] left-[4.17%] block h-[91.66%] w-[89.83%] max-w-none"
      />
    </span>
  );
}

/** Figma Mark/GitHub (101:140). */
function GitHubMark({ className }: { className?: string }) {
  return (
    <img src="/auth/github.svg" alt="" className={`size-5 shrink-0 ${className ?? ''}`} />
  );
}

type Provider = 'oauth_google' | 'oauth_github';

/**
 * Figma "Auth / OAuth buttons" (101:201): Google and GitHub as full-width
 * outline Large buttons. Each redirects to the provider through Clerk and comes
 * back to /sso-callback (carrying `redirect_url`, where it lands after). On
 * the sign-up screen they start a sign-up, elsewhere a sign-in; Clerk
 * transfers between the two when the account does or doesn't exist yet.
 */
export function OAuthButtons({ action }: { action: 'Continue' | 'Sign up' }) {
  const navigate = useNavigate();
  const { signIn } = useSignIn();
  const { signUp } = useSignUp();
  const target = useRedirectTarget();
  const [pending, setPending] = useState<Provider | null>(null);

  async function start(strategy: Provider) {
    setPending(strategy);
    const params = {
      strategy,
      redirectCallbackUrl: withRedirect('/sso-callback', target),
      redirectUrl: target,
    };
    const { error } =
      action === 'Sign up' ? await signUp.sso(params) : await signIn.sso(params);
    // On success the browser is already leaving for the provider.
    if (error) {
      setPending(null);
      await navigate('/sign-in/provider-error');
    }
  }

  return (
    <Stack gap={3}>
      <Button
        type="button"
        variant="outline"
        size="lg"
        leadingIcon={GoogleMark}
        className="w-full"
        loading={pending === 'oauth_google'}
        disabled={pending !== null}
        onClick={() => void start('oauth_google')}
      >
        {action} with Google
      </Button>
      <Button
        type="button"
        variant="outline"
        size="lg"
        leadingIcon={GitHubMark}
        className="w-full"
        loading={pending === 'oauth_github'}
        disabled={pending !== null}
        onClick={() => void start('oauth_github')}
      >
        {action} with GitHub
      </Button>
    </Stack>
  );
}
